import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { getCurrentUser, logout as logoutRequest } from '../api/auth';
import { getMyWallet } from '../api/profile';
import { tokenStorage } from '../api/storage';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [identity, setUser] = useState(null);
  const [seasonRole, setSeasonRole] = useState('MEMBER');
  const user = useMemo(() => identity ? { ...identity, platform_role: identity.role === 'ADMIN' ? 'ADMIN' : 'MEMBER', role: identity.role === 'ADMIN' ? 'ADMIN' : seasonRole } : null, [identity, seasonRole]);
  const [wallet, setWallet] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = useCallback(async () => {
    if (!tokenStorage.hasAccessToken()) {
      setUser(null);
      setWallet(null);
      setLoading(false);
      return;
    }

    try {
      const [currentUser, currentWallet] = await Promise.all([
        getCurrentUser(),
        getMyWallet()
      ]);
      setUser(currentUser);
      setWallet(currentWallet);
    } catch (error) {
      if (error.status === 401 || error.status === 403) {
        tokenStorage.clearTokens();
        setUser(null);
        setWallet(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refreshSession();
  }, [refreshSession]);

  const logout = useCallback(async () => {
    await logoutRequest();
    setUser(null);
    setWallet(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      wallet,
      loading,
      isAuthenticated: Boolean(user),
      refreshSession,
      setSeasonRole,
      logout
    }),
    [user, wallet, loading, refreshSession, logout]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used inside AuthProvider');
  }
  return context;
}
